import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import MotivationCardsView from "./MotivationCardsView";
import { BackImg } from "@utils/defaultImages";
import UploadIcon from "@assets/images/NoSubscriptionAstronot.png";
import { format } from "date-fns";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import { useNavigate } from "react-router-dom";

const DashboardSuggestion = ({
  data = [],
  setOpen,
  recreateLoading = false,
  subscription,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const itemRefs = useRef([]);

  // Get today's date and day
  const todayDate = new Date().toISOString().split("T")[0];
  const todayDay = new Date().toLocaleDateString("en-US", { weekday: "long" });

  // Find today's data index - this should be used ONLY for Daily tab
  const todayIndex = useMemo(() => {
    if (!data?.length) return 0;

    // Look for today's date
    const dateIndex = data.findIndex((item) => item.date === todayDate);
    if (dateIndex !== -1) return dateIndex;

    // Look for today's day
    const dayIndex = data.findIndex((item) => item.day === todayDay);
    if (dayIndex !== -1) return dayIndex;

    // Default to first item
    return 0;
  }, [data, todayDate, todayDay]);

  const [tab, setTab] = useState("Daily Motivation");
  // Separate states for each tab
  const [dailySelectedIndex, setDailySelectedIndex] = useState(todayIndex);
  const [weeklySelectedIndex, setWeeklySelectedIndex] = useState(todayIndex);

  // Get the current selected index based on active tab
  const selectedIndex =
    tab === "Daily Motivation" ? dailySelectedIndex : weeklySelectedIndex;

  // Get the correct setter function based on active tab
  const setSelectedIndex =
    tab === "Daily Motivation" ? setDailySelectedIndex : setWeeklySelectedIndex;

  // Get the data to display
  const displayData = useMemo(() => {
    if (!data?.length) return null;
    return data[selectedIndex];
  }, [data, selectedIndex]);

  // Check if displayData is for today (for heading)
  const isToday = useMemo(() => {
    if (!displayData) return false;
    return displayData.date === todayDate || displayData.day === todayDay;
  }, [displayData, todayDate, todayDay]);

  // Scroll to selected index for weekly tab
  useEffect(() => {
    if (tab === "Weekly Motivation" && itemRefs.current[weeklySelectedIndex]) {
      itemRefs.current[weeklySelectedIndex].scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [weeklySelectedIndex, tab]);

  // Update daily tab to today when data changes or if todayIndex changes
  useEffect(() => {
    setDailySelectedIndex(todayIndex);
  }, [todayIndex]);

  // Empty state
  if (!data?.length) {
    return (
      <div
        style={{ backgroundImage: `url(${BackImg})` }}
        className="rounded-[12px] p-6 flex flex-col items-center bg-cover mt-6"
      >
        <img
          src={UploadIcon}
          className="border border-white rounded-full p-1 min-h-[300px]"
          alt="No motivation data"
        />
        <h2 className="text-2xl font-bold text-white text-center mt-4">
          {t("Looks like you're ready for a reset.")}
        </h2>
        <p className="text-white my-2 text-center">
          {t("Regenerate your motivational coach's advice.")}
        </p>
        <button
          className="px-8 py-3 bg-[#64DDFF] rounded-full text-white font-semibold"
          onClick={() =>
            subscription?.status === "active" ||
            subscription?.status === "pending-cancel"
              ? setOpen(true)
              : navigate("/renew-subscription")
          }
        >
          {recreateLoading ? <ButtonLoader /> : t("Recreate Plan")}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-lg p-3 md:p-6 rounded-lg mt-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="font-semibold text-gray-800 text-lg">
          {t("Motivation Overview")}
        </h2>

        {/* Tabs */}
        <div className="flex bg-white rounded-full border border-gray-300">
          {["Daily Motivation", "Weekly Motivation"].map((item) => (
            <button
              key={item}
              onClick={() => setTab(item)}
              className={`px-3 md:px-6 py-2 rounded-full text-xs md:text-sm font-medium transition-all ${
                tab === item
                  ? "bg-gradient-to-r from-[#0771EF] to-[#15AAFC] text-white"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              {t(item)}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {tab === "Weekly Motivation" ? (
        <div className="border rounded-xl p-4 bg-white">
          {/* Day Selector - Only for Weekly tab */}
          <div className="flex overflow-x-auto mb-3 pb-2 gap-1 w-full max-w-[75vw] sm:max-w-[100%]">
            {data?.map((item, index) => {
              const isActive = weeklySelectedIndex === index;
              const isTodayItem =
                item.date === todayDate || item.day === todayDay;

              return (
                <div
                  key={index}
                  ref={(el) => (itemRefs.current[index] = el)}
                  className={`flex flex-col min-w-[70px] md:min-w-[80px] md:flex-1 rounded-[11px] py-[28px] md:px-[10px] cursor-pointer border gap-[10px]
          ${
            isActive
              ? "bg-gradient-to-r from-[#0771EF] to-[#15AAFC] text-white border-[#0575E6]"
              : "bg-white text-black border-white hover:border-[#0575E6] hover:text-[#0575E6]"
          }
        `}
                  onClick={() => setWeeklySelectedIndex(index)}
                >
                  <p className="text-[14px] font-[Lato] font-[400] text-center">
                    {t(
                      `dietPlanDashboard.allMeals.daysShort.${format(
                        new Date(item.date),
                        "EEE",
                      )}`,
                    )}
                  </p>
                  <p className="text-[14px] font-[Lato] text-center">
                    {format(new Date(item.date), "dd")}{" "}
                    {t(format(new Date(item.date), "MMM").toLowerCase())}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Motivation Cards */}
          <MotivationCardsView
            heading={`✨ ${t(`days.${displayData?.day?.toLowerCase()}`)} ${t("Motivation")} ✨`}
            content={displayData}
          />
        </div>
      ) : (
        // Daily Motivation (always shows today)
        <MotivationCardsView
          heading={`✨ ${t("today motivation")} ✨`}
          content={displayData}
        />
      )}
    </div>
  );
};

export default DashboardSuggestion;
